import { writable } from "svelte/store";
import type { ManageAPI } from "~/manage-api/types";
import { SearchIndex, type SourceIndex, search } from "./ngram";


export type POI = {
  /** Unique identifier for UI */
  key: string;

  /** ID that connects this POI to object on the map */
  id: string;

  /** Type of the POI */
  type: string;

  hidden?: boolean;

  /** If false no routing should be tried to/from of this POI */
  routable?: boolean;

  /** Label to be used on the map */
  label: string;

  /** Floor info in human readadble form */
  floor: string;

  /** Underlaying object related to this POI */
  data: unknown;
}

export type StorePOI = POI & {
  type: 'store',
  data: ManageAPI.Store
}

type TransformerToPOI<T,P extends POI> = {
  type: string;
  /** One item can generate more than one POIs */
  transform (item: T): POI[];
  indexes (poi: P): SourceIndex;
}

const store_transformer: TransformerToPOI<ManageAPI.Store, StorePOI> = {
  type: 'store',
  transform (store) {
    const space_codes = POIData.splitAndDecode(store.spaceCode);

    let floor = store.floor?.description;

    if (store.floor?.officeTower) {
      floor = `${store.floor.officeTower.description} · ${floor}`;
    }

    return space_codes.map(([type, sub_code, space_code]) => ({
      key: `${space_code}|${store.id}`,
      id: space_code,
      type: this.type,
      hidden: type == 'sub',
      label: store.name,
      floor,
      data: store,
    }));
  },
  indexes (poi) {
    const xs = [poi.label, poi.key.toString()];
    // const kws = poi.data?.keywords.split(/\s*[,;]\s*/) || [];
    // for (const k of kws) {
    //   xs.push(k, poi.id);
    // }
    return xs;
  }
};

// Handle Fob with scdata transformer + context

export class POIData {
  
  readonly map = new Map<string,POI>();
  readonly key_map = new Map<string,POI>();
  readonly list: POI[] = [];
  readonly list_filtered = writable<POI[]>([]);

  readonly search: SearchIndex;

  private transformers = new Map<string,TransformerToPOI<any,any>>();

  constructor () {
    this.search = new SearchIndex();
    this.addTransformer(store_transformer);
  }

  addTransformer (transformer: TransformerToPOI<any,any>) {
    this.transformers.set(transformer.type, transformer);
  }

  add<T>(xs: readonly T[], type: string): POI[] {
    
    const transformer = this.transformers.get(type) as TransformerToPOI<T,any>;
    if (!transformer) {
      console.warn(`No POI transformer for '${type}'`);
      return [];
    }

    let pois = new Array<POI>(xs.length);
    const source_indexes = [];

    for (const x of xs) {
      const _pois = transformer.transform(x);

      for (const poi of _pois) {

        const { id, key } = poi;
        // Maybe generated id and mark poi not routable
        // that way it could be still shown listing
        if (!id) {
          console.warn('POI missing id', poi);
          continue;
        }
        
        this.map.set(id, poi);
        this.key_map.set(key, poi);
        pois.push(poi);
      }
    } 
    
    // Clean empty space 
    pois = pois.filter(x => x);
    this.list.push(...pois);
    this.list_filtered.set(this.list);

    for (const p of pois) {
      source_indexes.push(...transformer.indexes(p));
    }

    this.search.updateSector(type, source_indexes);

    return pois;
  }

  filter (search_term: string) {
    if (!search_term) {
      this.list_filtered.set(this.list);
      return;
    }

    const source_index = this.search.index;
    const result_index = search(search_term, source_index);

    const filtered: POI[] = [];

    for (let index = 0; index < result_index.length; index += 2) {
      const key_index = result_index[index + 1];
      const key = source_index[key_index + 1];
      const poi = this.getPoiByKey(key);
      poi && filtered.push(poi);
    }
    this.list_filtered.set(filtered);
    
    // console.log(result_index);
    // console.log(filtered.map(x => x.label));
    // console.log(source_index);
  }

  getPoiByID (id: string) {
    return this.map.get(id);
  }

  getPoiByKey (key: string) {
    return this.key_map.get(key);
  }

  static splitAndDecode(space_code: string): SpaceCode[] {
    return POIData.splitCode(space_code).map(POIData.decodeSpaceCode);
  }

  static splitCode(space_code = '') {
    return space_code.split(/\s*,\s*/);
  }

  static readonly spacecode_regx = /^((sub|via):)?([^:]*)(::(.*))?/;

  static decodeSpaceCode (code = ''): SpaceCode {
    const [
      /* full match */,
      /* sub: (legacy  stuff that might not be in use even) */,
      type,
      space_code,
      /* ::subCode */,
      sub_code
    ] = code.match(POIData.spacecode_regx) || [];
    return [
      sub_code ? 'sub' : 'normal',
      sub_code,
      space_code
    ];
  }
}

type SpaceCode = [
  type: 'sub' | 'normal', 
  sub_code: string,
  space_code: string,
]
